import React, { useState, useEffect, useCallback } from 'react';
import { promoSlides, POPUP_VERSION, PromoCta, LocalizedString, LocalizedReactNode } from './promoSlides';
import { Language, PageVariant } from '../types';
import { TRANSLATIONS } from '../translations';

const TEST_MODE = true;

const POPUP_DELAY_MS = 5000;
const SLIDE_INTERVAL_MS = 5000;
const COOKIE_EXPIRY_DAYS = 7;
const COOKIE_TEMP_EXPIRY_HOURS = 24;
const POPUP_STATE_KEY = 'lesector_promo_state';
const POPUP_SESSION_KEY = 'lesector_promo_session_lock';

interface PromoPopupProps {
  language: Language;
  currentView?: PageVariant;
}

export default function PromoPopup({ language, currentView }: PromoPopupProps) {
  const resolveLocalized = <T extends any>(value: T | Partial<Record<Language, T>> | undefined): T | undefined => {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'object' && value !== null && !React.isValidElement(value) && !Array.isArray(value)) {
      const translations = value as Partial<Record<Language, T>>;
      return translations[language] || translations[Language.EN] || (Object.values(translations)[0] as T);
    }
    return value as T;
  };
  const [isOpen, setIsOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  
  // Trigger states
  const [hasScrolled, setHasScrolled] = useState(false);
  const [timeSpent, setTimeSpent] = useState(0);
  const [triggerFired, setTriggerFired] = useState(false);
  
  // Analytics Hooks (Internal)
  const trackPopupOpen = useCallback((slideId: string) => {
    // Hook for GA4 / Meta Pixel
    // console.log(`[Analytics] Popup Opened. Initial Slide: ${slideId}`);
  }, []);

  const trackPopupClose = useCallback(() => {
    // console.log(`[Analytics] Popup Closed.`);
  }, []);

  const trackSlideChange = useCallback((slideId: string) => {
    // console.log(`[Analytics] Slide Changed. New Slide: ${slideId}`);
  }, []);

  const trackCtaClick = useCallback((slideId: string, actionType: string, payload: string) => {
    // console.log(`[Analytics] CTA Clicked. Slide: ${slideId}, Action: ${actionType}, Payload: ${payload}`);
  }, []);

  const trackTriggerFired = useCallback((conditions: any) => {
    // console.log(`[Analytics] Trigger Fired. Conditions:`, conditions);
  }, []);

  // Filter Active Slides based on status, scheduling and priority
  const activeSlides = React.useMemo(() => {
    let slides = promoSlides.filter(s => {
      if (s.status !== 'enabled') return false;
      const now = new Date().getTime();
      if (s.startDate && new Date(s.startDate).getTime() > now) return false;
      if (s.endDate && new Date(s.endDate).getTime() < now) return false;
      return true;
    });

    if (slides.length === 0) return [];
    
    // 5. Campaign Priority (Highest priority wins)
    const maxPriority = Math.max(...slides.map(s => s.priority || 0));
    return slides.filter(s => (s.priority || 0) === maxPriority);
  }, []);
  
  // Touch handling for mobile swipe
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => {
      const nextIndex = prev === activeSlides.length - 1 ? 0 : prev + 1;
      if (activeSlides[nextIndex]) {
        trackSlideChange(activeSlides[nextIndex].id);
      }
      return nextIndex;
    });
  }, [activeSlides, trackSlideChange]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => {
      const prevIndex = prev === 0 ? activeSlides.length - 1 : prev - 1;
      if (activeSlides[prevIndex]) {
        trackSlideChange(activeSlides[prevIndex].id);
      }
      return prevIndex;
    });
  }, [activeSlides, trackSlideChange]);

  const closePopup = useCallback((isPermanent: boolean = false) => {
    setIsOpen(false);
    trackPopupClose();
    setTimeout(() => setIsRendered(false), 700); // Wait for fade out
    
    if (!TEST_MODE) {
      const now = new Date().getTime();
      const expiryDate = isPermanent 
        ? now + COOKIE_EXPIRY_DAYS * 24 * 60 * 60 * 1000 
        : now + COOKIE_TEMP_EXPIRY_HOURS * 60 * 60 * 1000;
        
      const state = {
        version: POPUP_VERSION,
        expiry: expiryDate,
        dismissType: isPermanent ? 'permanent' : 'temporary'
      };
      localStorage.setItem(POPUP_STATE_KEY, JSON.stringify(state));
    }
  }, [trackPopupClose]);

  const dismissPopup = useCallback(() => {
    closePopup(true);
  }, [closePopup]);

  const openPopup = useCallback(() => {
    setIsRendered(true);
    setTimeout(() => {
      setIsOpen(true);
      if (activeSlides[0]) {
        trackPopupOpen(activeSlides[0].id);
      }
    }, 50);
  }, [activeSlides, trackPopupOpen]);

  const handleCtaClick = (cta: PromoCta, slideId: string) => {
    trackCtaClick(slideId, cta.type, cta.payload);
    
    // CTA Action Handler
    switch(cta.type) {
      case 'url':
      case 'pdf':
        if (cta.target === '_blank') {
          window.open(cta.payload, '_blank');
        } else {
          window.location.href = cta.payload;
        }
        closePopup();
        break;
      case 'modal':
      case 'video':
      case 'custom':
        // Custom logic can be implemented here later
        closePopup();
        break;
      default:
        closePopup();
    }
  };

  // Intelligent Triggers
  useEffect(() => {
    if (currentView !== PageVariant.HOME) {
      // Reset trigger state when navigating away
      setIsOpen(false);
      setIsRendered(false);
      setTriggerFired(false);
      setTimeSpent(0);
      setHasScrolled(false);
      return;
    }

    const handleScroll = () => {
      setHasScrolled(true);
    };
    
    window.addEventListener('scroll', handleScroll, { once: true });
    
    const interval = setInterval(() => {
      setTimeSpent(prev => prev + 1000);
    }, 1000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearInterval(interval);
    };
  }, [currentView]);

  useEffect(() => {
    if (currentView !== PageVariant.HOME || activeSlides.length === 0 || triggerFired) return;
    
    let shouldCheckTriggers = true;
    
    if (!TEST_MODE) {
      // Session Lock check
      if (sessionStorage.getItem(POPUP_SESSION_KEY)) {
        shouldCheckTriggers = false; // Already fired in this session
      }

      const storedStateStr = localStorage.getItem(POPUP_STATE_KEY);
      if (storedStateStr) {
        try {
          const storedState = JSON.parse(storedStateStr);
          const now = new Date().getTime();
          
          // If version matches and we haven't expired, don't show
          if (storedState.version === POPUP_VERSION && storedState.expiry > now) {
            shouldCheckTriggers = false;
          }
        } catch (e) {
          // Fallback if invalid JSON
        }
      }
    }
    
    if (!shouldCheckTriggers) return;
    
    if (timeSpent >= 5000 || hasScrolled) {
      setTriggerFired(true);
      if (!TEST_MODE) {
        sessionStorage.setItem(POPUP_SESSION_KEY, 'true');
      }
      openPopup();
    }
  }, [currentView, activeSlides, timeSpent, hasScrolled, triggerFired, openPopup]);

  // Autoplay logic
  useEffect(() => {
    if (!isOpen || activeSlides.length <= 1 || isHovered) return;

    const interval = setInterval(() => {
      nextSlide();
    }, SLIDE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isOpen, activeSlides.length, isHovered, nextSlide]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closePopup(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closePopup]);

  const getTranslation = (key: 'open' | 'latestUpdates' | 'close' | 'prev' | 'next' | 'goto' | 'dontShowAgain') => {
    return TRANSLATIONS[language]?.promo?.[key] || TRANSLATIONS[Language.EN]?.promo?.[key] || '';
  };

  if (!isRendered) return null;

  return (
    <>
      {/* Main Popup Wrapper */}
      {isRendered && (
        <div 
          className={`fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6 transition-all duration-700 ease-out ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          role="dialog"
          aria-modal="true"
          aria-label={getTranslation('open')}
        >
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-neutral-900/40 backdrop-blur-md transition-opacity duration-700"
        onClick={() => closePopup(false)}
        aria-hidden="true"
      />

      {/* Modal Content */}
      <div 
        className={`group relative w-[calc(100vw-32px)] md:w-[90vw] lg:w-[85vw] max-w-[1600px] h-auto max-h-[95vh] md:h-[85vh] md:max-h-[900px] flex flex-col rounded-none shadow-2xl overflow-hidden transition-all duration-700 ease-out transform ${isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-8'} bg-white`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <button 
          onClick={() => closePopup(false)}
          className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center bg-black/10 hover:bg-black/20 text-white md:bg-black/5 md:hover:bg-black/10 md:text-neutral-900 rounded-none transition-colors focus:outline-none focus:ring-1 focus:ring-neutral-900 backdrop-blur-md"
          aria-label={getTranslation('close')}
        >
          <i className="fa-solid fa-xmark text-lg"></i>
        </button>

        <div className="relative w-full overflow-hidden flex-1 flex flex-col">
          <div 
            className="flex h-full transition-transform duration-500 ease-in-out w-full flex-1"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {activeSlides.map((slide) => {
              // Theme resolution
              const isDark = slide.theme === 'dark';
              const textPrimary = isDark ? 'text-white' : 'text-neutral-900';
              const textSecondary = isDark ? 'text-neutral-300' : 'text-neutral-600';
              const bgClass = isDark ? 'bg-neutral-900' : 'bg-white';
              
              return (
                <div key={slide.id} className={`w-full h-full flex-shrink-0 flex flex-col md:flex-row ${bgClass}`}>
                  {/* Image Area */}
                  <div className="w-full md:w-[70%] relative flex items-center justify-center bg-neutral-100 shrink-0 min-h-[40vh] md:min-h-0 md:h-full">
                    {/* Desktop Image */}
                    {slide.image && (
                      <img 
                        src={slide.image} 
                        alt={(resolveLocalized(slide.title) as string) || slide.name} 
                        className={`w-full h-full object-contain p-4 md:p-8 ${slide.mobileImage ? 'hidden md:block' : 'block'}`}
                        loading="lazy"
                      />
                    )}
                    {/* Mobile Image */}
                    {slide.mobileImage && (
                      <img 
                        src={slide.mobileImage} 
                        alt={(resolveLocalized(slide.title) as string) || slide.name} 
                        className="w-full h-auto max-h-[60vh] object-contain md:hidden"
                        loading="lazy"
                      />
                    )}
                  </div>
                  
                  {/* Text/Content Area */}
                  <div className="w-full md:w-[30%] shrink-0 flex-1 md:flex-none md:h-full p-8 md:p-10 lg:p-12 flex flex-col overflow-y-auto">
                    <div className="w-full my-auto flex flex-col items-start justify-start">
                    {slide.title && (
                      <h2 className={`text-3xl md:text-4xl font-heading uppercase tracking-widest mb-8 leading-tight ${textPrimary}`}>
                        {resolveLocalized(slide.title)}
                      </h2>
                    )}
                    
                    {slide.description && (
                      <p className={`mb-8 leading-relaxed text-sm ${textSecondary}`}>
                        {resolveLocalized(slide.description)}
                      </p>
                    )}
                    
                    {/* Rich Content Area */}
                    {slide.richContent && (
                      <div className={`mb-8 w-full text-sm leading-relaxed ${textSecondary}`}>
                        {resolveLocalized(slide.richContent)}
                      </div>
                    )}
                    
                    {/* CTAs */}
                    {slide.ctas && slide.ctas.length > 0 && (
                      <div className="flex flex-col sm:flex-row gap-4 w-full mt-auto md:mt-0 pt-4 md:pt-0">
                        {slide.ctas.map((cta, i) => {
                          const isPrimary = i === 0;
                          let buttonStyle = '';
                          
                          if (isDark) {
                            buttonStyle = isPrimary 
                              ? 'bg-[#C5A059] text-white hover:bg-[#b38c4b]'
                              : 'text-white/70 hover:text-white hover:bg-white/10';
                          } else {
                            buttonStyle = isPrimary
                              ? 'bg-[#C5A059] text-white hover:bg-[#b38c4b]'
                              : 'text-neutral-500 hover:text-neutral-900 hover:bg-neutral-50';
                          }

                          return (
                            <button 
                              key={i}
                              onClick={() => handleCtaClick(cta, slide.id)}
                              className={`inline-flex justify-center items-center px-8 py-5 rounded-none transition-all duration-300 ease-out transform uppercase tracking-widest text-xs font-bold w-full sm:w-auto focus:outline-none ${isPrimary ? 'hover:-translate-y-1 hover:shadow-lg' : 'hover:opacity-80'} ${buttonStyle}`}
                            >
                              {resolveLocalized(cta.label)}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Permanent Dismiss (Don't show again) */}
                    <button
                      onClick={dismissPopup}
                      className={`mt-6 text-xs uppercase tracking-widest underline underline-offset-4 transition-colors opacity-70 hover:opacity-100 focus:outline-none ${textSecondary}`}
                    >
                      {getTranslation('dontShowAgain')}
                    </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Carousel Controls */}
          {activeSlides.length > 1 && (
            <>
              {/* Prev / Next Buttons (Desktop mostly) */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white/90 hover:bg-white text-neutral-900 rounded-none shadow-sm opacity-0 md:group-hover:opacity-100 transition-all duration-300 hidden md:flex focus:outline-none"
                aria-label={getTranslation('prev')}
              >
                <i className="fa-solid fa-chevron-left text-sm"></i>
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white/90 hover:bg-white text-neutral-900 rounded-none shadow-sm opacity-0 md:group-hover:opacity-100 transition-all duration-300 hidden md:flex focus:outline-none"
                aria-label={getTranslation('next')}
              >
                <i className="fa-solid fa-chevron-right text-sm"></i>
              </button>

              {/* Pagination Dots */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3 z-10">
                {activeSlides.map((slide, index) => {
                  const isDark = slide.theme === 'dark';
                  const activeClass = isDark ? 'bg-white w-8' : 'bg-neutral-900 w-8';
                  const inactiveClass = isDark ? 'bg-white/40 hover:bg-white/60 w-2' : 'bg-neutral-300 hover:bg-neutral-400 w-2';
                  
                  return (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentSlide(index);
                        trackSlideChange(activeSlides[index].id);
                      }}
                      className={`h-1 transition-all duration-300 ease-out focus:outline-none ${currentSlide === index ? activeClass : inactiveClass}`}
                      aria-label={`${getTranslation('goto')} ${index + 1}`}
                    />
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    )}
    </>
  );
}

